/**
 * THE SHOWCASE DEMO LANE, CLIENT SIDE (th-mpimer.17; launch-marketing-pages design M7
 * "Members" and "The map follows the lane"; stress pass 3 ruling L1).
 *
 * One leaf module holds what puts this tab's requests on a showcase lane, and the one
 * lane identity the map follows:
 *
 *   - the member pass, which the tour player sets while it is live on a corpus showcase
 *     source (th-jcvmrh.9) and clears at the tour's end and on any abort path. It is
 *     minted by `POST /api/v2/showcases/:key/demo_pass` and kept under the showcase KEY
 *     it was minted for; the server reads the lane from the signed pass itself, so the
 *     only thing a request carries is `X-Showcase-Pass`;
 *   - the invisible guest token (th-mpimer.26), which `services/guestToken.js` mints
 *     after a guest's first genuine interaction and hands here;
 *   - the lane identity, `none`, `guest` or `pass:<key>`, published to
 *     `onLaneChange` listeners once per change. `stores/mapTileQuery.js` bumps the tile
 *     epoch on it (th-mpimer.12), so the tiles in view reload under the new lane.
 *
 * `services/sessionToken.js#authorizationHeaders` merges `laneHeaders()` after the
 * bearer, so every v2 API request, every same-origin map tile and the tour resolver
 * carry the pass (and the guest token) while it is held, and nothing once it is dropped.
 *
 * IN MEMORY ONLY. A closed tab or a crash ends the pass with the page; a cookie or
 * storage would make it ambient across tabs for its whole life (pass 2 B3). Nothing on
 * the client needs to survive it: the member keeps the window the center paid
 * (founder F1), and a token minted during the tour keeps lane-covered elements in its
 * manifest (lead L2).
 *
 * THE ONE END SEAM. Every window `authTransition`, whatever its type (`guestToUser`,
 * `signOut`, `userSwitch` and the rest), drops what this module holds. Nothing else
 * clears it on an identity change. `onLaneEnd` listeners run there too, once per
 * transition, synchronously inside its dispatch and whether or not the identity moved:
 * a tour that cannot cross an identity change (`stores/guidedTour.js#showcaseSource`)
 * ends there, with no `authTransition` listener of its own.
 *
 * THE GUEST ARM (th-mpimer.26). The invisible guest token lands HERE and nowhere else:
 * held beside the pass, sent as `X-Guest-Token` from `laneHeaders()`, published as the
 * identity `guest` (the pass wins while both are held, as the server reads only the
 * pass for an actor), and dropped by the same `authTransition` seam. Like the pass it
 * drops itself at its `expiresAt`; `guestToken.js` hears the identity leave `guest`
 * and re-arms its interaction listener, so the next interaction re-mints.
 *
 * A LEAF. It imports nothing from the app (the boot-graph rule, th-jki5kv.8): the tour
 * player sets the pass here, never by being imported, so `sessionToken.js` stays a
 * leaf that imports only leaves.
 */

export const SHOWCASE_PASS_HEADER = "X-Showcase-Pass";
export const GUEST_TOKEN_HEADER = "X-Guest-Token";

export const LANE_NONE = "none";
export const LANE_GUEST = "guest";

// The member pass while one is held: { pass, showcaseKey, expiresAt }, else null.
let held = null;
let expiryTimer = null;
// The guest token while one is held: { token, expiresAt }, else null.
let guest = null;
let guestExpiryTimer = null;
const listeners = new Set();
const endListeners = new Set();

/**
 * This tab's lane identity: `pass:<showcase key>` while a pass is held, else `guest`
 * while a guest token is held, else `none`.
 */
export function laneIdentity() {
  if (held) return `pass:${held.showcaseKey}`;
  return guest ? LANE_GUEST : LANE_NONE;
}

/**
 * The headers that put a request on the lane: `X-Showcase-Pass` while a pass is held,
 * `X-Guest-Token` while a guest token is held, an empty object with neither. Spread it
 * into a header bag.
 */
export function laneHeaders() {
  return {
    ...(guest ? { [GUEST_TOKEN_HEADER]: guest.token } : {}),
    ...(held ? { [SHOWCASE_PASS_HEADER]: held.pass } : {}),
  };
}

/** Is a guest token held? */
export function holdsGuestToken() {
  return guest != null;
}

/**
 * Listen for lane identity changes. `listener(identity)` runs once per change, never
 * for a write that leaves the identity where it stood.
 *
 * @param {(identity: string) => void} listener
 * @returns {() => void} unsubscribe
 */
export function onLaneChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Listen for the one end seam: `listener(type)` runs on every window `authTransition`,
 * whatever its type, synchronously inside its dispatch, once the lane is dropped, and
 * whether or not the identity moved (a guest whose token expired moves from `none` to
 * `none`).
 *
 * @param {(type: string|undefined) => void} listener handed the transition's type
 * @returns {() => void} unsubscribe
 */
export function onLaneEnd(listener) {
  endListeners.add(listener);
  return () => endListeners.delete(listener);
}

// Run `change`, then tell the listeners when it moved the identity.
function publishing(change) {
  const before = laneIdentity();
  change();
  const after = laneIdentity();
  if (after === before) return;
  [...listeners].forEach((listener) => listener(after));
}

// A timer that runs `expire` at `expiresAt` (at once when it is past), or null when
// no instant is stated.
function expiryTimerAt(expiresAt, expire) {
  const remainingMs = expiresAt ? Date.parse(expiresAt) - Date.now() : NaN;
  return Number.isFinite(remainingMs) ? setTimeout(expire, Math.max(remainingMs, 0)) : null;
}

function dropPass() {
  clearTimeout(expiryTimer);
  expiryTimer = null;
  held = null;
}

function dropGuestToken() {
  clearTimeout(guestExpiryTimer);
  guestExpiryTimer = null;
  guest = null;
}

/**
 * Hold the member pass the mint answered. A pass past its `expiresAt` is refused by the
 * server and takes no lane, so the module drops it then, and the map leaves the lane
 * with it.
 *
 * @param {string} pass the signed pass (`data.pass`)
 * @param {{ showcaseKey: string, expiresAt?: string }} options the showcase key it was
 *   minted for, and the mint's `data.expires_at`
 */
export function setShowcasePass(pass, { showcaseKey, expiresAt } = {}) {
  if (!pass || !showcaseKey) throw new TypeError("[showcaseLane] a pass needs its pass and its showcase key");
  publishing(() => {
    dropPass();
    held = { pass, showcaseKey, expiresAt: expiresAt ?? null };
    expiryTimer = expiryTimerAt(expiresAt, clearShowcasePass);
  });
}

/** Drop the member pass: the tour ended or aborted, or the pass expired. */
export function clearShowcasePass() {
  publishing(dropPass);
}

/**
 * Hold the guest token the mint answered (`services/guestToken.js`). A token past its
 * `expiresAt` takes no lane, so the module drops it then.
 *
 * @param {string} token the signed guest token (`data.token`)
 * @param {{ expiresAt?: string }} options the mint's `data.expires_at`
 */
export function setGuestToken(token, { expiresAt } = {}) {
  if (!token) throw new TypeError("[showcaseLane] a guest token needs its token");
  publishing(() => {
    dropGuestToken();
    guest = { token, expiresAt: expiresAt ?? null };
    guestExpiryTimer = expiryTimerAt(expiresAt, clearGuestToken);
  });
}

/** Drop the guest token: it expired. */
export function clearGuestToken() {
  publishing(dropGuestToken);
}

// THE ONE END SEAM: an identity change drops everything this module holds, then
// tells the `onLaneEnd` listeners.
function endLane(event) {
  publishing(() => {
    dropPass();
    dropGuestToken();
  });
  [...endListeners].forEach((listener) => listener(event?.detail?.type));
}

if (typeof window !== "undefined") {
  window.addEventListener("authTransition", endLane);
}
